"use client";

import clsx from "clsx";
import { motion, Variants } from "framer-motion";
import { useId, useState } from "react";
import styles from "./TabsBlock.module.scss";
import { kebabCase } from "@/utilities/string-helpers";

interface TabsBlockProps {
	className?: string;
	tabs?: Array<{
		label: string;
		content: React.ReactNode;
	}>;
	customId?: string;
}

export const TabsBlock = ({ className, tabs, customId }: TabsBlockProps) => {
	const [activeTab, setActiveTab] = useState(0);
	const tabsId = useId();

	const variants: Variants = {
		inactive: {
			opacity: 0,
			display: "none",
		},
		active: {
			opacity: 1,
			display: "block",
		},
	};

	return (
		<section
			id={customId ? kebabCase(customId) : undefined}
			className={clsx(styles["tabs"], className)}
			data-block="tabs"
		>
			<div className={styles["tabs__nav"]}>
				{tabs &&
					tabs.map((tab, index) => {
						const panelId = `${tabsId}-panel-${index}`;

						return (
							<button
								key={index}
								onClick={() => setActiveTab(index)}
								aria-controls={panelId}
								aria-expanded={index === activeTab}
								role="tab"
							>
								{tab.label}
							</button>
						);
					})}
			</div>

			{tabs &&
				tabs.map((tab, index) => {
					const panelId = `${tabsId}-panel-${index}`;

					return (
						<motion.div
							variants={variants}
							initial={index === activeTab ? "active" : "inactive"}
							animate={index === activeTab ? "active" : "inactive"}
							key={index}
							id={panelId}
							role="tabpanel"
							className={styles["tabs__panel"]}
							data-expanded={index === activeTab}
						>
							{tab.content}
						</motion.div>
					);
				})}
		</section>
	);
};
